/**
 * @typedef {object} QuizResponse
 * @property {OpenEndedQuizAnswer|MultipleChoiceQuizAnswer} result undocumented
 * @property {"multipleChoiceQuestion"|"openEndedQuestion"} quizType undocumented
 * @property {boolean} isLessonItemCompleted undocumented
 * @property {boolean} isLessonCompleted undocumented
 * @property {Link} links undocumented
 */

/**
 * @typedef {object} OpenEndedQuizAnswer
 * @property {string} acceptedText undocumented
 * @property {number} potentialPointsAdded undocumented
 */

/**
 * @typedef {object} MultipleChoiceQuizAnswer
 * @property {number} correctChoice undocumented
 * @property {number} pointsAdded undocumented
 */

/**
 * @typedef {object} Link
 * @property {string|null} update undocumented
 */

/**
 * @typedef {object} CourseAnswerSubmittedEventDetails
 * @property {boolean} isLessonItemCompleted undocumented
 * @property {boolean} isLessonCompleted undocumented
 * @property {number} lessonId undocumented
 * @property {number} lessonItemId undocumented
 * @property {HTMLElement|null} quizResultElement null for OEQ
 */

import {Notification} from '../../modules/notification.js';
import {requestDialog} from '../../modules/dialog.js';
import {sanitizeHtml} from '../../utils/markupHelpers.js';
import {buildElement} from '../../modules/htmlElementBuilder.js';
import {getHttpClient} from '../../modules/httpClient.js';

/** @type {boolean|undefined} */
let hasScorePointsSection;

/**
 * Get random quote
 * @returns {HTMLElement}
 */
export function getRandomQuote() {
    const quotes = [
        {
            html: '<i>“Anyone who has never made a mistake has never tried anything new”<br>—Albert Einstein</i>',
        },
        {
            html: '<i>“Even Thomas Edison failed 1,000 times before successfully creating the lightbulb!”</i>',
        },
        {
            html: '<i>“Your best teacher is your last mistake.”<br>—Ralph Nader</i>',
        },
        {
            html: `It's not how we make mistakes, but how we correct them that defines us.`,
        },
        {
            html: '<i>“Mistakes are, after all, the foundations of truth.”<br>—Carl Jung</i>',
        },
        {
            html: '<i>“Failure is success if we learn from it.”<br>—Malcolm Forbes</i>',
        },
        {
            html: '<i>“The only real mistake is the one from which we learn nothing.”<br>—Henry Ford</i>',
        },
        {
            html: '<i>“Mistakes – call them unexpected learning experiences.”<br>—Richard Bach</i>',
        },
        {
            html: `<i>“Don't waste a good mistake… learn from it.”<br>—Robert Kiyosaki</i>`,
        },
    ];

    const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];

    return buildElement('p')
        .addClass('quiz-answer-quote', 'mt-small', 'mb-none', 'text-color-secondary', 'text-left')
        .addHtml(randomQuote.html)
        .getElement();
}

/**
 * Calculate number of words on the given text.
 * Separators:
 * - whitespaces
 * - new lines
 *
 * @param {string} inputString
 * @returns {number} - number of words in the string
 * @private
 */
function countWords(inputString) {
    inputString = inputString.replaceAll(/(^\s*)|(\s*$)/gi, ''); // exclude  start and end white-space

    inputString = inputString.replaceAll(/ {2,}/gi, ' '); // 2 or more spaces to 1
    inputString = inputString.replace(/\n /, '\n'); // exclude newline with a start spacing
    if (inputString === '') {
        return 0;
    }

    return inputString.split(' ').length;
}

/**
 * @returns {boolean}
 */
function hasScorePoints() {
    if (hasScorePointsSection !== undefined) {
        return hasScorePointsSection;
    }

    hasScorePointsSection = !!document.querySelector('[data-quiz-section][data-has-points]');
    return hasScorePointsSection;
}

/**
 * Get the Correct Answer Message
 * @param {number} pointsEarned
 * @returns {HTMLElement}
 */
function getCorrectAnswerMessage(pointsEarned) {
    return buildElement('div')
        .addClass('message', 'message--correct')
        .addHtml(
            '<p class="quizResult__text mb-none">Correct!</p>' +
            (hasScorePoints() ?
   `<p class="mb-none ml-tiny quizResult__points">+${pointsEarned} ${
                pointsEarned > 1 ? 'points' : 'point'
            } towards your certificate</p>
    <div class="achievementProgressbar__part quizResult__progressBar js_quizResult__progressBar"></div>
    ` : '')
        )
        .getElement();
}

/**
 * Get Incorrect Answer Message
 * @returns {HTMLElement}
 */
function getIncorrectAnswerMessage() {
    return buildElement('div')
        .addClass('message', 'message--incorrect')
        .addHtml('<p class="quizResult__text mb-none">Your answer was incorrect!</p>')
        .getElement();
}

/**
 * Show Multiple Choice Answer
 * @param {HTMLElement} form
 * @param {number} point
 * @param {number} correctAnswer
 * @param {number} answeredChoice
 */
function showMcqAnswer(form, point, correctAnswer, answeredChoice) {
    const isUserAnsweredCorrectly = correctAnswer === answeredChoice;

    const resultMessage = isUserAnsweredCorrectly ? getCorrectAnswerMessage(point) : getIncorrectAnswerMessage();
    const quizResultContainer = form.querySelector('.js-quizResult');
    quizResultContainer.append(resultMessage);
    quizResultContainer.classList.remove('hidden');

    if (!isUserAnsweredCorrectly) {
        const quote = getRandomQuote();
        const answerResultAnimationDuration = 500;
        setTimeout(function () {
            quizResultContainer.append(quote);
        }, answerResultAnimationDuration + 300);
    }

    /** mark labels as correct or incorrect */
    const $correctChoice = form.querySelector(`input.js-answerChoice[value="${correctAnswer}"]`);
    const $label = $correctChoice.nextElementSibling;
    $label.classList.add('quizzes__choiceLabel--correct');
    $label.append('(Correct answer)');

    if (!isUserAnsweredCorrectly) {
        const $answeredChoice = form.querySelector(`input[value="${answeredChoice}"]`);
        const answerChoiceLabel = $answeredChoice.nextElementSibling;
        answerChoiceLabel.classList.add('quizzes__choiceLabel--incorrect');
    }
}

/**
 * Display result
 * @param {HTMLElement} form
 * @param {number} point
 * @param {number} correctAnswer
 */
function afterAnswerSubmitDisplayMcqResult(form, point, correctAnswer) {
    /** {HTMLInputElement} */
    const $answerChoice = form.querySelector('input:checked');
    const answeredChoice =  parseInt($answerChoice.value);

    showMcqAnswer(form, point, correctAnswer, answeredChoice);
    showInstructorExplanation(form);
}

/**
 * Display result
 * @param {HTMLElement} form
 * @param {string} acceptedText
 */
function afterAnswerSubmitDisplayOeqResult(form, acceptedText) {
    // hide RTE and display submitted html in a readonly div
    form.querySelector('rt-editor').classList.add('hidden');
    // Show user's answer at the pseudo-disabled input
    form.querySelector('.js-quizAnswered').innerHTML = acceptedText;
    form.querySelector('.js-quizAnswered').classList.remove('hidden');
    // Show await-grading message
    form.querySelector('.js-quizAwaitGradingMessage').classList.remove('hidden');
    form.querySelector('.js-quizEdit')?.classList.remove('hidden');
}

/**
 * Display result
 * @param {HTMLFormElement} form
 * @param {Link} links
 */
function afterAnswerSubmitUpdateUrl(form, links) {
    if (links.update === null) {
        return;
    }

    updateFormActionAndMethod(form, links.update, 'PUT');
}

/**
 * @param {HTMLFormElement} form
 * @param {string} action
 * @param {string} method
 */
function updateFormActionAndMethod(form, action, method) {
    form.action = action;

    let methodInput = form.querySelector('input[name="_method"]');
    if (methodInput === null) {
        methodInput = document.createElement('input');
        methodInput.setAttribute('type', 'hidden');
        methodInput.setAttribute('name', '_method');

        form.prepend(methodInput);
    }

    methodInput.value = method;
}

/**
 * @param {HTMLFormElement} form
 */
function showInstructorExplanation(form) {
    const instructorExplanation = form.querySelector('.js-instructorExplanation');
    if (instructorExplanation) instructorExplanation.classList.remove('hidden');
}

/**
 * Submit Answer (for any Quiz type) using AJAX request
 * (the form should be validated before calling this method, {@see validateAndSubmitOEQAnswer}).
 *
 * @param {HTMLFormElement} form
 */
async function submitValidatedAnswer(form) {
    const formData = new FormData(form);

    const quizId = form.dataset.quizId;
    const submitButton = form.querySelector('button[type="submit"]');

    formData.append('answer', formData.get(quizId));
    formData.delete(quizId); // deduplicate data

    submitButton.disabled = true;
    enableWindowUnloadEvent(false);

    /** @type {QuizResponse} */
    const response = await getHttpClient()
        .post(form.action, formData)
        .catch((error) => {
            form.querySelector('button').disabled = true;

            const errorThrown = error.message;
            const errorMessage =
                error.status === 401
                    ? 'Failed to submit quiz answer: save your answer and re-login to try again.'
                    : `Failed to submit quiz answer: ${errorThrown}`;
            Notification.error(errorMessage, form);
        });

    if (response) {
        const lessonId = Number(document.querySelector('#lessonId').value);
        const lessonItemId = Number(document.querySelector('#lessonItemId').value);

        // common for all types of quizzes
        form.querySelectorAll('input[type="radio"]').forEach((radioButton) => {
            radioButton.disabled = true;
        });
        form.querySelectorAll('.js-quizSubmit').forEach((submitButton) => {
            submitButton.classList.add('hidden');
        });
        form.querySelector('.js-quizRemarks')?.classList.add('hidden');
        form.querySelector('.js-quiz-after-submit-message')?.classList.remove('hidden');

        /**
         * trigger custom event, that can be listened by any element,
         * panels, like lessonNavigator or rankingPanel
         */
        switch (response.quizType) {
            case 'multipleChoiceQuestion':
                afterAnswerSubmitDisplayMcqResult(form, response.result.pointsAdded, response.result.correctChoice);
                break;
            case 'openEndedQuestion':
                afterAnswerSubmitDisplayOeqResult(form, response.result.acceptedText);
                break;
            default:
                throw new Error('Unknown quiz type.');
        }

        afterAnswerSubmitUpdateUrl(form, response.links);
        determineToAnimateNextLessonButton();

        /** @type {CourseAnswerSubmittedEventDetails} */
        const answerSubmittedEventData = {
            result: {
                pointsAdded: response.result.pointsAdded || 0,
                potentialPointsAdded: response.result.potentialPointsAdded || 0,
            },
            isLessonItemCompleted: response.isLessonItemCompleted,
            isLessonCompleted: response.isLessonCompleted,
            lessonId: lessonId,
            lessonItemId: lessonItemId,
            quizResultElement: form.querySelector('.js-quizResult'),
        };

        document.dispatchEvent(new CustomEvent('courseAnswerSubmitted', {
            detail: answerSubmittedEventData,
        }));
    }

    [...form.querySelectorAll('.ixdf-spinner')].forEach((button) =>
        button.classList.remove('button--loading', 'is-sending')
    );
}

/**
 * Validate and submit (is valid) form with one quiz answer.
 * @param {Event} submitEvent - submit event, where target - form
 */
async function submitMCQAnswer(submitEvent) {
    submitEvent.preventDefault();

    /** @type {HTMLFormElement} */
    const form = submitEvent.target;

    submitValidatedAnswer(form);
}

/**
 * Validate and submit (is valid) form with one quiz answer.
 * @param {Event} submitEvent - submit event, where target - form
 */
async function validateAndSubmitOEQAnswer(submitEvent) {
    submitEvent.preventDefault();

    /** @type {HTMLFormElement} */
    const form = submitEvent.target;
    const textArea = form.querySelector('textarea');

    const wordsRequiredByConfirmDialog = 10;
    const textareaStripText = sanitizeHtml(textArea.value);
    if (countWords(textareaStripText) < wordsRequiredByConfirmDialog) {
        // If confirmation required - display a popup window and bring handler to confirmation button
        const result = await requestDialog(form.dataset.confirmationId);
        if (result !== 'CONFIRM_ACTION') {
            // Remove spinner from submit button if it is not in progress of submitting an answer
            [...form.querySelectorAll('.ixdf-spinner:not(.is-sending)')].forEach((button) =>
                button.classList.remove('button--loading')
            );
            // Do not submit form until member confirmed it
            return;
        }
        // Add the state class `is-sending` to know we're processing the answer.
        form.querySelector('.ixdf-spinner').classList.add('is-sending');
    }

    submitValidatedAnswer(form);
}

/**
 * Activate/deactivate a window unload event to prevent data loose before leaving the page
 * after some changes have been made by a user.
 *
 * @param {boolean} isEnabled
 */
function enableWindowUnloadEvent(isEnabled) {
    if (isEnabled) {
        // eslint-disable-next-line unicorn/prefer-add-event-listener
        window.onbeforeunload = function () {
            return 'If you leave the page some changes may not be saved.';
        };
    } else {
        // eslint-disable-next-line unicorn/prefer-add-event-listener
        window.onbeforeunload = null;
    }
}

/**
 * Activate Form Submit Button on radio checked event
 * @param {Event} radioCheckedEvent
 */
function activateFormSubmitButton(radioCheckedEvent) {
    const form = radioCheckedEvent.target.closest('form');
    const submitButton = form.querySelector('.js-quizSubmit');

    submitButton.removeAttribute('disabled');
    enableWindowUnloadEvent(true);
}

/**
 * @param {HTMLElement} form
 */
function activateReattemptForm(form) {
    form.querySelector('.js-quizAnswered')?.classList.add('hidden');
    form.querySelector('.js-quizResult')?.classList.add('hidden');
    form.querySelector('.js-quizReattempt')?.classList.add('hidden');
    form.querySelector('.js-quizEdit')?.classList.add('hidden');
    form.querySelector('textarea[disabled]')?.removeAttribute('disabled');
    form.querySelector('rt-editor')?.classList.remove('hidden');
}

/**
 * Handler is triggerent when quiz form clicked
 * @param {HTMLElement} quizForm closest form element
 * @param {HTMLElement} eventTarget element that fired click event
 */
function clickQuizForm(quizForm, eventTarget) {
    const editQuizButton = eventTarget.closest('.js-quizEdit');
    if (editQuizButton) {
        const submitButton = quizForm.querySelector('.js-quizSubmitChanges');
        submitButton.disabled = true;
        submitButton.classList.remove('hidden');

        activateReattemptForm(quizForm);
    } else {
        const reAttemptButton = eventTarget.closest('.js-quizReattempt');
        if (reAttemptButton) {
            quizForm.querySelector('.js-quizSubmitAnswer').classList.remove('hidden');

            activateReattemptForm(quizForm);
        }
    }
}

/**
 * Handler is triggered when quiz RTE content changed
 * @param {HTMLElement} richTextEditor
 */
function changeRteContent(richTextEditor) {
    const textarea = richTextEditor.querySelector('textarea');
    const textareaStripText = sanitizeHtml(richTextEditor.value);
    const canSaveChanges = countWords(textareaStripText) >= textarea.minLength;

    enableWindowUnloadEvent(canSaveChanges);

    textarea.form.querySelectorAll('.js-quizSubmit').forEach((submitButton) => {
        submitButton.disabled = !canSaveChanges;
    });
}

/** [no description; please add] */
function determineToAnimateNextLessonButton() {
    const mcqQuizForm = [...document.querySelectorAll('.js-mcqForm')];
    const oeqQuizForm = [...document.querySelectorAll('.js-oeqForm')];

    if (mcqQuizForm.length === 0 && oeqQuizForm.length === 0) {
        highlightNextButton();
    } else {
        const gradedMCQQuizAnswer = [...document.querySelectorAll('.js-mcqForm .js-quizResult .message')];
        const gradedOEQQuizAnswer = [...document.querySelectorAll('.js-oeqForm .js-quizResult .message')];
        const awaitingQuizAnswer = [...document.querySelectorAll('.js-oeqForm .js-quizAwaitGradingMessage:not(.hidden)')];
        if (gradedMCQQuizAnswer.length + gradedOEQQuizAnswer.length + awaitingQuizAnswer.length === mcqQuizForm.length) {
            highlightNextButton();
        }
    }
}

/** [no description; please add] */
function highlightNextButton() {
    const buttonNextLesson = document.querySelector('.js-buttonNextLesson');
    if (buttonNextLesson) {
        buttonNextLesson.classList.remove('button--ghost');
        buttonNextLesson.classList.add('button--primary');
    }
}

/**
 * Init page, register event handlers
 */
function initPage() {
    document.querySelectorAll('.js-mcqForm').forEach((quizForm) => {
        quizForm.addEventListener('submit', submitMCQAnswer);
        quizForm.querySelectorAll('.js-quizMultiChoiceRadioInput').forEach((radioInput) => {
            radioInput.addEventListener('change', activateFormSubmitButton);
        });
    });
    document.querySelectorAll('.js-oeqForm').forEach((quizForm) => {
        quizForm.addEventListener('submit', validateAndSubmitOEQAnswer);
        quizForm.addEventListener('click', (event) => {
            clickQuizForm(quizForm, event.target);
        });
        quizForm.addEventListener('rte:change', (event) => {
            changeRteContent(event.target);
        });
    });

    determineToAnimateNextLessonButton();
}

// init page when the document is ready
document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', initPage) : initPage();
